import {
  PARENT_FORM_CATEGORY_SLUG,
  PARTICIPANT_PROFILE_TABS,
  USER_ROLE,
} from "Constants/constant";
import { BLK_PORTAL_PREMISSION_KEYS_CHILDREN } from "store/constants";
import {
  categoryAppliesToFormModule,
  isCustomFormCategory,
  orderFormCategoriesCustomAfterOther,
} from "components/DynamicForms/shared/formCategoryUtils";
import { formCategoryTabFromCustomCategory } from "./participantProfileCrmCategoryTabs.utils";

const DEFAULT_ROLE_ACCESS = [
  USER_ROLE.Sp_Admin,
  USER_ROLE.Sp_Standard_User,
  USER_ROLE.Sp_manager,
];

/** Tab routing ids for platform CRM categories (must match ParticipantProfileTabs switch). */
const CRM_PLATFORM_FORM_TAB_META = [
  {
    slug: PARENT_FORM_CATEGORY_SLUG.PARTICIPANT_ASSESSMENT.SLUG,
    id: 6,
    permissions:
      BLK_PORTAL_PREMISSION_KEYS_CHILDREN.PARTICIPANT.FORM.ASSESSMENT,
  },
  {
    slug: PARENT_FORM_CATEGORY_SLUG.RISK_MANAGEMENT.SLUG,
    id: 7,
  },
  {
    slug: PARENT_FORM_CATEGORY_SLUG.INJURY_MANAGEMENT.SLUG,
    id: 9,
  },
  {
    slug: PARENT_FORM_CATEGORY_SLUG.HIGH_CARE.SLUG,
    id: 8,
  },
  {
    slug: PARENT_FORM_CATEGORY_SLUG.MEDICATION.SLUG,
    id: 5,
  },
  {
    slug: PARENT_FORM_CATEGORY_SLUG.SUPPORT_PLAN.SLUG,
    id: 15,
  },
  {
    slug: PARENT_FORM_CATEGORY_SLUG.PERSON_CENTERED_PLAN.SLUG,
    id: 14,
  },
  {
    slug: PARENT_FORM_CATEGORY_SLUG.Exit_Transaction.SLUG,
    id: 18,
  },
  {
    slug: PARENT_FORM_CATEGORY_SLUG.Other.SLUG,
    id: 25,
  },
];

const normalizeCategorySlug = (slug) => String(slug ?? "").toLowerCase();

let participantFormsSubTabsCache = null;

export const setParticipantProfileFormsSubTabs = (children) => {
  participantFormsSubTabsCache = Array.isArray(children) ? children : null;
};

export const matchPlatformFormTabMeta = (category) => {
  const slug = normalizeCategorySlug(category?.slug ?? category);
  return CRM_PLATFORM_FORM_TAB_META.find((meta) => {
    const base = meta.slug.toLowerCase();
    return (
      slug === base ||
      slug.endsWith(`-${base}`) ||
      slug.endsWith(`-crm-${base}`)
    );
  });
};

const platformCategoryName = (meta) => {
  const key = Object.keys(PARENT_FORM_CATEGORY_SLUG).find(
    (k) => PARENT_FORM_CATEGORY_SLUG[k].SLUG === meta.slug
  );
  return key ? PARENT_FORM_CATEGORY_SLUG[key].NAME : meta.slug;
};

const slugMatchesParent = (childSlug, parentSlug) => {
  const normalizedParent = normalizeCategorySlug(parentSlug);
  const normalizedChild = normalizeCategorySlug(childSlug);
  return (
    normalizedChild === normalizedParent ||
    normalizedChild.endsWith(`-${normalizedParent}`) ||
    normalizedChild.endsWith(`-crm-${normalizedParent}`)
  );
};

/** Resolve participant profile Forms sub-tab after fill/save (supports tenant-prefixed category slugs). */
export const resolveParticipantFormSubTabByParentSlug = (parentSlug) => {
  if (!parentSlug) {
    return false;
  }

  const normalized = normalizeCategorySlug(parentSlug);
  if (normalized === PARENT_FORM_CATEGORY_SLUG.RISK_MANAGEMENT.SLUG) {
    return PARTICIPANT_PROFILE_TABS.find((tab) => tab.id === 11) ?? false;
  }

  const cached = participantFormsSubTabsCache;
  if (cached?.length) {
    const match = cached.find((child) => slugMatchesParent(child.slug, parentSlug));
    if (match) {
      return match;
    }
  }

  const meta = matchPlatformFormTabMeta({ slug: parentSlug });
  if (meta) {
    return {
      name: platformCategoryName(meta),
      id: meta.id,
      children: [],
      slug: meta.slug,
      roleAccess: DEFAULT_ROLE_ACCESS,
      ...(meta.permissions ? { permissions: meta.permissions } : {}),
    };
  }

  const formsTab = PARTICIPANT_PROFILE_TABS.find((tab) => tab.id === 4);
  if (!formsTab?.children?.length) {
    return false;
  }

  return (
    formsTab.children.find((child) => slugMatchesParent(child.slug, parentSlug)) ||
    false
  );
};

/**
 * Build Forms submenu from CRM form-categories API (same order as Forms Management CRM).
 */
export const buildParticipantProfileFormsTabChildren = (categories = []) => {
  const crmCategories = categories.filter((category) =>
    categoryAppliesToFormModule(category, "crm")
  );
  const ordered = orderFormCategoriesCustomAfterOther(crmCategories);

  return ordered
    .map((category) => {
      if (isCustomFormCategory(category)) {
        return formCategoryTabFromCustomCategory(category);
      }

      const meta = matchPlatformFormTabMeta(category);
      if (!meta) {
        return null;
      }

      return {
        name: category.name,
        id: meta.id,
        children: [],
        slug: meta.slug,
        roleAccess: DEFAULT_ROLE_ACCESS,
        ...(meta.permissions ? { permissions: meta.permissions } : {}),
      };
    })
    .filter(Boolean);
};
